import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ChevronDown, ChevronLeft } from "lucide-react";
import { compareActivities, listComparisonCandidates } from "../lib/api";
import { useAuth } from "../lib/auth";
import { compareCandidateDisplay, sameSportCandidates } from "../lib/activityComparison";
import { ActivityCompareTable } from "../components/ActivityCompareTable";

export const ActivityCompare = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const unitSystem = user?.unit_system || "metric";
  const otherId = searchParams.get("with") || "";
  const [activities, setActivities] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [error, setError] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingCompare, setLoadingCompare] = useState(false);
  const [open, setOpen] = useState(false);
  const pickerRef = useRef(null);

  const current = useMemo(
    () => activities.find((row) => row.id === id) || null,
    [activities, id],
  );
  const candidates = useMemo(() => sameSportCandidates(activities, current), [activities, current]);
  const currentDisplay = current ? compareCandidateDisplay(current) : null;
  const selected = useMemo(
    () => candidates.find((row) => row.id === otherId) || null,
    [candidates, otherId],
  );
  const selectedDisplay = selected ? compareCandidateDisplay(selected) : null;

  useEffect(() => {
    let cancelled = false;
    setLoadingList(true);
    listComparisonCandidates()
      .then((rows) => {
        if (!cancelled) setActivities(rows || []);
      })
      .catch(() => {
        if (!cancelled) setError("Could not load activities.");
      })
      .finally(() => {
        if (!cancelled) setLoadingList(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    if (!otherId) {
      setComparison(null);
      setLoadingCompare(false);
      return undefined;
    }
    setLoadingCompare(true);
    setError(null);
    compareActivities(id, otherId)
      .then((payload) => {
        if (!cancelled) setComparison(payload);
      })
      .catch((err) => {
        if (!cancelled) {
          setComparison(null);
          setError(err?.response?.data?.detail || "Those activities could not be compared.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingCompare(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, otherId]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const onPick = (value) => {
    setOpen(false);
    if (!value) {
      setSearchParams({});
      return;
    }
    setSearchParams({ with: value });
  };

  return (
    <div className="space-y-6">
      <Link
        to={`/activity/${id}`}
        className="inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase text-dark-text-muted hover:text-dark-text-primary transition-colors"
        data-testid="back-to-activity-link"
      >
        <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
        Back to activity
      </Link>
      <header className="space-y-2">
        <div className="text-[10px] tracking-[0.3em] uppercase text-dark-text-muted">Compare</div>
        <h1 className="font-heading text-3xl uppercase tracking-tight text-dark-text-primary">
          Compare two {current?.sport || "activities"}
        </h1>
        <p className="text-sm text-dark-text-secondary">
          Choose another {current?.sport || "activity"} of the same sport. Missing numbers stay blank. Samples are loaded only for these two activities.
        </p>
      </header>

      <div className="relative block space-y-2 max-w-md" ref={pickerRef}>
        <span className="text-[10px] tracking-[0.16em] uppercase text-dark-text-muted">Compared activity</span>
        <button
          type="button"
          data-testid="compare-activity-select"
          aria-label="Compared activity"
          aria-haspopup="listbox"
          aria-expanded={open}
          disabled={loadingList || !candidates.length}
          onClick={() => setOpen((value) => !value)}
          className="app-control w-full h-auto min-h-0 py-2 text-left flex items-center justify-between gap-2"
        >
          <span className="min-w-0 flex-1">
            {selectedDisplay ? (
              <>
                <span className="block text-sm text-dark-text-primary">{selectedDisplay.name}</span>
                <span className="block text-xs text-dark-text-muted">{selectedDisplay.when}</span>
              </>
            ) : (
              <span className="block text-sm text-dark-text-muted">Select an activity</span>
            )}
          </span>
          <ChevronDown className="w-4 h-4 shrink-0 text-dark-text-muted" strokeWidth={1.5} />
        </button>
        {open ? (
          <div
            className="absolute z-20 mt-1 w-full max-w-md max-h-72 overflow-y-auto space-y-1 rounded-md border border-dark-border bg-dark-surface p-1"
            data-testid="compare-activity-dropdown"
            role="listbox"
            aria-label="Compared activity"
          >
            {candidates.map((row) => {
              const display = compareCandidateDisplay(row);
              const isSelected = otherId === row.id;
              return (
                <button
                  key={row.id}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => onPick(row.id)}
                  data-testid={`compare-candidate-${row.id}`}
                  className={`w-full text-left app-control h-auto min-h-0 py-2 ${
                    isSelected ? "border-[color:var(--brand-primary)]" : ""
                  }`}
                >
                  <span className="block text-sm text-dark-text-primary">{display.name}</span>
                  <span
                    className="block text-xs text-dark-text-muted"
                    data-testid={`compare-candidate-${row.id}-when`}
                  >
                    {display.when}
                  </span>
                </button>
              );
            })}
          </div>
        ) : null}
      </div>

      {currentDisplay ? (
        <p className="text-sm text-dark-text-secondary" data-testid="compare-current-name">
          This activity: {currentDisplay.name}
          <span className="block text-xs text-dark-text-muted">{currentDisplay.when}</span>
        </p>
      ) : null}

      {error ? (
        <p className="text-sm text-analytics-red" data-testid="compare-error">{error}</p>
      ) : null}
      {!loadingList && !candidates.length ? (
        <p className="text-sm text-dark-text-secondary" data-testid="compare-empty">
          There is no other {current?.sport || "activity"} of the same sport to compare yet.
        </p>
      ) : null}
      {loadingCompare ? (
        <p className="text-sm text-dark-text-secondary">Loading comparison.</p>
      ) : null}
      {comparison ? <ActivityCompareTable comparison={comparison} unitSystem={unitSystem} /> : null}
    </div>
  );
};
